import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Loader2, ArrowLeft, Mail } from "lucide-react";

export function ResetPasswordPage() {
  const { resetPassword } = useAuth();

  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { error: authError } = await resetPassword(email);

      if (authError) {
        setError(authError.message);
        return;
      }

      setSent(true);
    } catch {
      setError("Ein unerwarteter Fehler ist aufgetreten. Bitte versuche es erneut.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6">
        {/* Logo */}
        <div className="flex flex-col items-center gap-2">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-vajra-blue to-vajra-cyan shadow-lg shadow-vajra-blue/25">
            <span className="text-xl font-bold text-white">6T</span>
          </div>
        </div>

        <Card>
          <CardHeader className="space-y-1">
            {sent ? (
              <>
                <div className="flex justify-center mb-2">
                  <Mail className="h-12 w-12 text-primary" />
                </div>
                <CardTitle className="text-xl text-center">
                  E-Mail gesendet
                </CardTitle>
                <CardDescription className="text-center">
                  Falls ein Konto mit{" "}
                  <span className="font-medium text-foreground">{email}</span>{" "}
                  existiert, haben wir dir einen Link zum Zurücksetzen des
                  Passworts gesendet.
                </CardDescription>
              </>
            ) : (
              <>
                <CardTitle className="text-xl">Passwort zurücksetzen</CardTitle>
                <CardDescription>
                  Gib deine E-Mail-Adresse ein und wir senden dir einen Link zum
                  Zurücksetzen deines Passworts.
                </CardDescription>
              </>
            )}
          </CardHeader>
          <CardContent>
            {sent ? (
              <div className="space-y-3">
                <Link to="/login">
                  <Button variant="outline" className="w-full">
                    <ArrowLeft className="h-4 w-4" />
                    Zurück zum Login
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  className="w-full text-muted-foreground"
                  onClick={() => {
                    setSent(false);
                    setEmail("");
                  }}
                >
                  Andere E-Mail verwenden
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">E-Mail</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    disabled={loading}
                  />
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Senden...
                    </>
                  ) : (
                    "Link senden"
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        {!sent && (
          <p className="text-center text-sm text-muted-foreground">
            <Link
              to="/login"
              className="inline-flex items-center gap-1 font-medium text-primary hover:text-primary/80 transition-colors"
            >
              <ArrowLeft className="h-3 w-3" />
              Zurück zum Login
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
